import React, { useState } from 'react';
import axios from 'axios';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline "Forgot Password" dialog used from the AuthPage sign-in card.
 *
 * Mounts in 2 states:
 *   - form   : email input + "Send reset link" button
 *   - sent   : success confirmation message (the backend always returns
 *              200 to prevent account enumeration, so we never branch
 *              on whether the email is registered or not).
 *
 * The user can close the dialog at any time; reopening starts back at
 * the form view with the previously-entered email pre-filled.
 */
export default function ForgotPasswordDialog({
  open,
  onOpenChange,
  initialEmail = '',
  accentColor = '#0f172a',
}) {
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Keep the email field in sync with the parent when the dialog re-opens.
  React.useEffect(() => {
    if (open) {
      setEmail(initialEmail || '');
      setSent(false);
    }
  }, [open, initialEmail]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = (email || '').trim();
    if (!trimmed) {
      toast.error('Please enter your account email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: trimmed,
        // Tell the backend where the user actually opened the app from so the
        // emailed link points back to the public URL (not the internal proxy
        // hostname the request happens to travel through). The backend
        // validates this against its trusted host allowlist before using it.
        frontend_url: typeof window !== 'undefined' ? window.location.origin : undefined,
      });
      setSent(true);
      toast.success(
        res?.data?.message ||
          'If an account exists for that email, a reset link is on its way.'
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || JSON.stringify(d)).join('; ')
            : null;
      toast.error(msg || 'Could not send reset link. Please try again in a minute.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Reset your password
          </DialogTitle>
          <DialogDescription>
            {sent
              ? "We've sent reset instructions to your inbox if that email is registered."
              : "Enter the email you use to sign in and we'll email you a one-time reset link."}
          </DialogDescription>
        </DialogHeader>

        {!sent ? (
          <form onSubmit={submit} className="space-y-4 mt-1">
            <div className="space-y-1.5">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
                data-testid="forgot-password-email-input"
                className="h-11"
              />
              <p className="text-xs text-slate-500">
                The link expires in 60 minutes and can be used only once.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                data-testid="forgot-password-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                style={{ backgroundColor: accentColor, color: 'white' }}
                data-testid="forgot-password-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  'Send reset link'
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4 mt-1" data-testid="forgot-password-sent-confirmation">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              <div className="font-semibold mb-1">Check your inbox</div>
              <p className="leading-relaxed">
                If an account exists for <span className="font-medium">{email}</span>, you'll
                receive an email shortly with a button to reset your password. It may take a
                minute and could land in the spam folder.
              </p>
            </div>
            <div className="text-xs text-slate-500">
              Didn't receive anything?
              <button
                type="button"
                onClick={() => setSent(false)}
                className="ml-1 underline hover:opacity-80"
                style={{ color: accentColor }}
                data-testid="forgot-password-retry-btn"
              >
                Try a different email
              </button>
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="forgot-password-close-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to sign in
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
